import { useMemo } from "react";
import axios from "axios";
import { API } from "../../App";
import { Badge } from "@/components/ui/badge";
import { GripVertical, ArrowUpDown, ArrowUp, ArrowDown, Bell } from "lucide-react";

const getPriorityBadgeClass = (priority) => {
  const classes = {
    low: "bg-green-100 text-green-700",
    medium: "bg-yellow-100 text-yellow-700",
    high: "bg-orange-100 text-orange-700",
    critical: "bg-red-100 text-red-700"
  };
  return classes[priority] || classes.medium;
};

const getStatusBadgeClass = (status) => {
  const classes = {
    new: "bg-blue-100 text-blue-700 hover:bg-blue-200",
    in_progress: "bg-purple-100 text-purple-700 hover:bg-purple-200",
    pending: "bg-yellow-100 text-yellow-700 hover:bg-yellow-200",
    closed: "bg-gray-100 text-gray-700 dark:text-white hover:bg-gray-200"
  };
  return classes[status] || classes.new;
};

const renderCell = (ticket, columnId, ticketsWithNotifications) => {
  switch (columnId) {
    case "ticket":
      return (
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-semibold text-slate-700 dark:text-white">
            {ticket.ticket_number}
          </span>
          {ticketsWithNotifications.has(ticket.id) && (
            <span className="relative flex items-center" data-testid={`ticket-notification-indicator-${ticket.id}`}>
              <Bell className="w-4 h-4 text-amber-500 animate-pulse" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
            </span>
          )}
        </div>
      );
    case "created_at":
      return (
        <span className="text-sm text-slate-600 dark:text-white">
          {new Date(ticket.created_at).toLocaleDateString()}
        </span>
      );
    case "priority":
      return (
        <Badge className={`text-xs ${getPriorityBadgeClass(ticket.priority)}`}>
          {ticket.priority}
        </Badge>
      );
    case "summary":
      return (
        <span className="text-sm text-slate-800 dark:text-white truncate block max-w-xs">
          {ticket.title}
        </span>
      );
    case "created_by":
      return (
        <span className="text-sm text-slate-600 dark:text-white truncate block max-w-[140px]">
          {ticket.created_by_name}
        </span>
      );
    case "assigned_to":
      return (
        <span className="text-sm text-slate-600 dark:text-white truncate block max-w-[140px]">
          {ticket.assigned_to_name || "Unassigned"}
        </span>
      );
    case "status":
      return (
        <Badge className={`text-xs ${getStatusBadgeClass(ticket.status)}`}>
          {ticket.status?.replace("_", " ")}
        </Badge>
      );
    default:
      return null;
  }
};

export const ITTicketTable = ({
  columns,
  setColumns,
  draggedColumn,
  setDraggedColumn,
  sortColumn,
  sortDirection,
  handleSort,
  paginatedTickets,
  ticketsWithNotifications,
  setTicketsWithNotifications,
  saveColumnPreferences,
  navigate,
  selectedTickets = new Set(),
  onToggleSelect,
  onToggleSelectAll,
  bulkMode = false,
}) => {
  const handleDragStart = (e, columnId) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    if (draggedColumn === columnId) return;
  };

  const handleDrop = (e, targetColumnId) => {
    e.preventDefault();
    if (draggedColumn === targetColumnId) return;

    const newColumns = [...columns];
    const draggedIndex = newColumns.findIndex(col => col.id === draggedColumn);
    const targetIndex = newColumns.findIndex(col => col.id === targetColumnId);
    const [removed] = newColumns.splice(draggedIndex, 1);
    newColumns.splice(targetIndex, 0, removed);

    setColumns(newColumns);
    saveColumnPreferences(newColumns);
    setDraggedColumn(null);
  };

  const visibleColumns = useMemo(() => columns.filter(col => col.visible), [columns]);

  const allVisibleSelected = paginatedTickets.length > 0 && paginatedTickets.every(t => selectedTickets.has(t.id));

  return (
    <div className="bg-white dark:bg-[#2C2C2C] rounded-lg shadow-md overflow-hidden" data-testid="table-view">
      <div className="px-4 py-2 bg-slate-100 dark:bg-[#343434] border-b border-slate-200 dark:border-slate-600">
        <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2">
          <GripVertical className="w-3 h-3" />
          Drag columns to reorder - Click column name to sort
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-[#343434] border-b border-slate-200 dark:border-slate-600">
            <tr>
              {bulkMode && (
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={() => onToggleSelectAll && onToggleSelectAll(paginatedTickets)}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    data-testid="bulk-select-all"
                  />
                </th>
              )}
              {visibleColumns.map((column) => (
                <th
                  key={column.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, column.id)}
                  onDragOver={(e) => handleDragOver(e, column.id)}
                  onDrop={(e) => handleDrop(e, column.id)}
                  className={`px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase tracking-wider cursor-move select-none hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors ${
                    draggedColumn === column.id ? "opacity-50 bg-blue-50 dark:bg-blue-900" : ""
                  }`}
                  style={{
                    width: column.id === 'ticket' ? '150px' : column.id === 'created_at' ? '110px' : 'auto'
                  }}
                >
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-3 h-3 text-slate-400" />
                    <span
                      onClick={(e) => { e.stopPropagation(); handleSort(column.id); }}
                      className="flex items-center gap-1 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                      data-testid={`sort-it-${column.id}`}
                    >
                      {column.label}
                      {sortColumn === column.id ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-40" />
                      )}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-600">
            {paginatedTickets.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + (bulkMode ? 1 : 0)} className="px-4 py-8 text-center text-slate-500 dark:text-white">
                  No tickets found
                </td>
              </tr>
            ) : (
              paginatedTickets.map((ticket) => (
                <tr
                  key={ticket.id}
                  className={`hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors ${
                    selectedTickets.has(ticket.id) ? "bg-blue-50 dark:bg-blue-900/30" : ""
                  }`}
                  onClick={() => {
                    if (bulkMode) {
                      onToggleSelect && onToggleSelect(ticket.id);
                      return;
                    }
                    if (ticketsWithNotifications.has(ticket.id)) {
                      axios.post(`${API}/notifications/mark-read-by-ticket/${ticket.id}`).catch(() => {});
                      setTicketsWithNotifications(prev => {
                        const next = new Set(prev);
                        next.delete(ticket.id);
                        return next;
                      });
                    }
                    navigate(`/tickets/${ticket.id}`);
                  }}
                  data-testid={`table-ticket-${ticket.id}`}
                >
                  {bulkMode && (
                    <td className="px-3 py-4 w-10" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedTickets.has(ticket.id)}
                        onChange={() => onToggleSelect && onToggleSelect(ticket.id)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        data-testid={`bulk-select-${ticket.id}`}
                      />
                    </td>
                  )}
                  {visibleColumns.map((column) => (
                    <td
                      key={column.id}
                      className="px-4 py-4 whitespace-nowrap"
                      style={{
                        width: column.id === 'ticket' ? '150px' : column.id === 'created_at' ? '110px' : 'auto'
                      }}
                    >
                      {renderCell(ticket, column.id, ticketsWithNotifications)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
